<template>
  <div class="bg-gray-50 min-h-screen text-left font-sans p-6 lg:p-10">
    
    <!-- 1. LOADING STATE -->
    <div v-if="isLoading" class="flex flex-col items-center justify-center h-64">
      <div class="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
      <p class="mt-4 font-semibold text-gray-400 uppercase tracking-wider text-xs">Fetching Benchmarks...</p>
    </div>

    <!-- 2. PENDING / NO DATA STATE -->
    <div v-else-if="isPending || !evalData" class="flex flex-col items-center justify-center h-96 bg-white rounded-2xl border border-gray-200 p-12 shadow-sm max-w-2xl mx-auto mt-10">
      <div class="bg-yellow-100 p-5 rounded-full mb-5">
        <svg class="w-10 h-10 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
      </div>
      <h2 class="text-xl font-bold text-gray-800">Evaluation Pending</h2>
      <p class="text-gray-500 mt-2 text-center text-sm leading-relaxed">
        Your role evaluation hasn't been finalized by HR yet. Once your benchmarks are set, your personalized salary report will appear here.
      </p>
    </div>

    <!-- 3. MAIN DASHBOARD CONTENT -->
    <div v-else class="max-w-6xl mx-auto space-y-6">
      
      <!-- HEADER -->
      <div>
        <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Compensation</h1>
      </div>

      <!-- TOP SUMMARY CARDS -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        
        <!-- Role Card -->
        <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col justify-center">
          <div class="text-sm text-gray-500 mb-2">Role</div>
          <div class="text-xl font-bold text-gray-900 mb-1 leading-tight">{{ roleMetadata.title || 'Backend Engineer' }}</div>
          <div class="text-sm text-gray-400">{{ orgName }} · Software Engineering</div>
        </div>
        
        <!-- Career Level Card -->
        <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col justify-center">
          <div class="text-sm text-gray-500 mb-2">Career Level</div>
          <div class="text-xl font-bold text-gray-900 mb-1 leading-tight">{{ roleMetadata.level || 'Individual Performer' }}</div>
          <div class="text-sm text-gray-400">Salary band {{ formatK(roleMetadata.min_salary) }} – {{ formatK(roleMetadata.max_salary) }}</div>
        </div>

        <!-- Job Grade Card -->
        <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col justify-center">
          <div class="flex justify-between items-center mb-2">
            <div class="text-sm text-gray-500">Job Grade</div>
            <!-- Standardized Pill Badge -->
            <div class="px-2.5 py-1 border border-gray-800 rounded-full text-xs font-semibold text-gray-900">
              Score {{ totalCalculatedScore }}
            </div>
          </div>
          <div class="text-xl font-bold text-gray-900 mb-1 leading-tight">{{ activeGrade.id }}</div>
          <div class="text-sm text-gray-400">Band position {{ activeGrade.position }}%</div>
        </div>

        <!-- NEW UPDATED SALARY CARD -->
        <!-- Salary Card -->
        <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col justify-between">
          
          <!-- Top Row: Label & Pill Badge -->
          <div class="flex justify-between items-center mb-3">
            <div class="text-sm text-gray-500 font-medium">Salary</div>
            <div style="border: 1px solid #111827; border-radius: 9999px; padding: 2px 10px; font-size: 12px; font-weight: 600; color: #111827; background-color: #ffffff;">
              {{ activeGrade.position }}% of band
            </div>
          </div>
          
          <!-- Middle Row: Large Salary Number -->
          <div class="text-3xl font-extrabold text-gray-900 mb-3 tracking-tight">
            {{ formatFullCurr(calculatedSalary) }}
          </div>
          
          <!-- Bottom Row: The Loading Bar (6px height) -->
          <div style="width: 100%; height: 6px; background-color: #f3f4f6; border-radius: 9999px; overflow: hidden;">
            <div :style="{ width: activeGrade.position + '%' }" 
                 style="height: 100%; background-color: #eab308; border-radius: 9999px; transition: width 0.7s ease;">
            </div>
          </div>
          
        </div>

      </div>

      <!-- BOTTOM ROW -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        <!-- TABLE -->
        <div class="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
          <div class="p-6 border-b border-gray-100 flex justify-between items-start">
            <div>
                <h3 class="font-bold text-gray-900 text-lg">Job evaluation of your role</h3>
                <p class="text-sm text-gray-500 mt-1">{{ roleMetadata.level }} · {{ roleMetadata.title }} · six weighted factors, scored 1-5</p>
            </div>
            <!-- Score Badge -->
            <div class="text-gray-900 px-3 py-1 rounded-full text-sm font-bold border border-gray-200 shadow-sm">
              {{ totalCalculatedScore }} / 5.00
            </div>
          </div>
          
          <div class="overflow-x-auto">
            <table class="w-full text-left border-collapse">
              <thead>
                <tr class="text-sm text-gray-500">
                  <th class="px-6 py-4 font-normal w-2/5">Factor</th>
                  <th class="px-6 py-4 font-normal text-center w-1/5">Weight</th>
                  <th class="px-6 py-4 font-normal text-center w-1/5">Score</th>
                  <th class="px-6 py-4 font-normal text-right w-1/5">Weighted score</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100 border-t border-gray-100">
                <tr v-for="item in evalData.criteria_data" :key="item.name" class="hover:bg-gray-50 transition-colors">
                  <td class="px-6 py-4 text-gray-800 font-medium text-sm">{{ item.name }}</td>
                  <td class="px-6 py-4 text-center text-gray-600 text-sm">{{ item.weight }}%</td>
                  <!-- DOTS UI -->
                  <td class="px-6 py-4">
                    <div class="flex items-center justify-center space-x-1">
                      <div v-for="i in 5" :key="i" 
                           class="w-3 h-3 rounded-full border border-gray-800" 
                           :class="i <= Math.round(item.previewScore || item.score || 0) ? 'bg-primary' : 'bg-transparent'">
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 text-right font-semibold text-gray-900 text-sm">
                    {{ ((item.weight * (item.previewScore || item.score || 0)) / 100).toFixed(2) }}
                  </td>
                </tr>
                <!-- Overall Row -->
                <tr class="bg-gray-50">
                  <td class="px-6 py-4 text-gray-900 font-bold text-sm">Overall</td>
                  <td class="px-6 py-4 text-center font-bold text-gray-900 text-sm">100%</td>
                  <td class="px-6 py-4"></td>
                  <td class="px-6 py-4 text-right font-bold text-gray-900 text-sm">{{ totalCalculatedScore }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- MAIN SLIDER BOX -->
        <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col h-full relative">
          <!-- Top Header -->
          <div class="flex justify-between items-center mb-1">
            <h3 class="font-bold text-gray-900 text-lg">Compensation position</h3>
            <div class="bg-yellow-500 text-gray-900 px-3 py-1 text-xs font-bold rounded-full border border-gray-900">
                {{ activeGrade.id }}
            </div>
          </div>
          
          <div class="text-sm text-gray-500 mb-6">{{ roleMetadata.level || 'Individual Performer' }} · EUR annual gross</div>
          
          <!-- Large Salary Display -->
          <div class="text-4xl font-extrabold text-gray-900 tracking-tight mb-8">
            {{ formatFullCurr(calculatedSalary) }}
          </div>

          <!-- SLIDER IMPLEMENTATION -->
          <div class="relative w-full mb-8 px-2">
            <!-- Track Background (Beige) -->
            <div class="w-full h-3 bg-orange-100 rounded-full relative flex items-center">
              
              <!-- Middle 50% dot marker -->
              <div class="absolute w-1 h-1 bg-gray-400 rounded-full left-1/2 transform -translate-x-1/2 z-0"></div>

              <!-- Yellow Fill -->
              <div class="h-full bg-yellow-200 rounded-l-full transition-all duration-700 z-10"
                   :style="{ width: activeGrade.position + '%' }"></div>

              <!-- Thumb (Circle with border) -->
              <div class="absolute w-5 h-5 bg-primary rounded-full border-2 border-gray-900 transform -translate-x-1/2 shadow-sm transition-all duration-700 z-20"
                   :style="{ left: activeGrade.position + '%' }"></div>
            </div>

            <!-- Axis Labels (€10k ... €15k ... €20k) -->
            <div class="flex justify-between items-center text-xs text-gray-500 mt-3 font-medium px-1">
              <span>{{ formatK(roleMetadata.min_salary) }}</span>
              <span>{{ formatK((roleMetadata.min_salary + roleMetadata.max_salary) / 2) }}</span>
              <span>{{ formatK(roleMetadata.max_salary) }}</span>
            </div>
          </div>

          <!-- DETAILED LIST -->
          <div class="flex-grow flex flex-col space-y-4 pt-4">
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">Career level</span>
              <span class="text-sm font-bold text-gray-900">{{ roleMetadata.level }}</span>
            </div>
            
            <div class="border-t border-gray-100"></div>
            
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">Salary band</span>
              <span class="text-sm font-bold text-gray-900">{{ formatK(roleMetadata.min_salary) }} – {{ formatK(roleMetadata.max_salary) }}</span>
            </div>
            
            <div class="border-t border-gray-100"></div>

            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">Minimum</span>
              <span class="text-sm font-bold text-gray-900">{{ formatFullCurr(roleMetadata.min_salary) }}</span>
            </div>
            
            <div class="border-t border-gray-100"></div>

            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">Maximum</span>
              <span class="text-sm font-bold text-gray-900">{{ formatFullCurr(roleMetadata.max_salary) }}</span>
            </div>
            
            <div class="border-t border-gray-100"></div>

            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">Job grade</span>
              <span class="text-sm font-bold text-gray-900">{{ activeGrade.id }}</span>
            </div>
            
            <div class="border-t border-gray-100"></div>

            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">Band position</span>
              <span class="text-sm font-bold text-gray-900">{{ activeGrade.position }}%</span>
            </div>
            
            <div class="border-t border-gray-100 pt-4 mt-2">
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-500">Current position</span>
                <span class="text-sm font-bold text-gray-900">{{ formatFullCurr(calculatedSalary) }}</span>
              </div>
            </div>
          </div>

          <!-- Bottom Text / Info -->
          <div class="mt-8">
            <p class="text-xs text-gray-500 leading-relaxed">
              Career Level defines the range. Job Grade defines the position. Band position is where your evaluated job sits in the range — it is not a performance measure. Band ranges are shared openly; individual salaries of colleagues are never shown.
            </p>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "UserCompensation",
  data() {
    return {
      isLoading: true,
      isPending: false,
      evalData: null,
      jobGrades: [],
      roleMetadata: {
          title: "Loading...",
          level: "...",
          min_salary: 0,
          max_salary: 0
      },
      orgName: "Workspace"
    };
  },
  computed: {
    totalCalculatedScore() {
      if (!this.evalData || !this.evalData.criteria_data) return "0.00";
      const total = this.evalData.criteria_data.reduce(function(acc, item) {
        const score = item.previewScore || item.score || 0;
        return acc + (item.weight * score / 100);
      }, 0);
      return total.toFixed(2);
    },
    activeGrade() {
      if (!this.evalData || this.jobGrades.length === 0) return { id: '...', position: 0 };
      const score = parseFloat(this.totalCalculatedScore);
      const gradesCopy = this.jobGrades.slice();
      const matchingGrade = gradesCopy.reverse().find(function(g) { return score >= g.minScore; });
      return matchingGrade || this.jobGrades[0];
    },
    calculatedSalary() {
      if (!this.roleMetadata.min_salary) return 0;
      const range = this.roleMetadata.max_salary - this.roleMetadata.min_salary;
      const positionFactor = this.activeGrade.position / 100;
      return this.roleMetadata.min_salary + (range * positionFactor);
    }
  },
  async created() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      try {
        const auth = this.$store.state.auth.user;
        const userEmail = auth ? auth.email : null;
        const userId = auth ? auth._id : null;
        
        const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
        const headers = { 'Authorization': 'Bearer ' + token };
        const API_URL = process.env.VUE_APP_API_URL;

        const orgsRes = await fetch(API_URL + '/api/v2/user/' + userEmail + '/active-organizations', { headers: headers });
        const orgsData = await orgsRes.json();
        
        if (!orgsData.data || orgsData.data.length === 0) throw new Error("No orgs found");

        let foundEval = null;
        let foundGrades = null;
        let foundBands = null;
        let foundOrgName = "Organization";
        let finalSelfId = null;

        for (let i = 0; i < orgsData.data.length; i++) {
            const org = orgsData.data[i];
            const mongoOrgId = org.organizationId;
            const bandsRes = await fetch(API_URL + '/api/v2/organization/' + mongoOrgId + '/salary-bands', { headers: headers });
            const bandsJson = await bandsRes.json();
            
            if (!bandsJson.data || bandsJson.data.length === 0) continue;

            for (let j = 0; j < bandsJson.data.length; j++) {
                const role = bandsJson.data[j];
                const selfId = role.role_id;
                const evalRes = await fetch(API_URL + '/api/v2/user-evaluations/' + mongoOrgId + '/' + selfId + '/' + userId, { headers: headers });
                const evalJson = await evalRes.json();

                if (evalJson.status === "success" && evalJson.data && evalJson.data.criteria_data) {
                    foundEval = evalJson.data;
                    finalSelfId = selfId;
                    
                    const gradesRes = await fetch(API_URL + '/api/v2/organization/' + mongoOrgId + '/job-grades', { headers: headers });
                    const gJson = await gradesRes.json();
                    foundGrades = gJson.data;
                    foundBands = bandsJson.data;
                    foundOrgName = org.organizationName;
                    break; 
                }
            }
            if (foundEval) break;
        }

        if (foundEval) {
          this.evalData = foundEval;
          this.jobGrades = foundGrades || [];
          this.orgName = foundOrgName;
          if (foundBands) {
             const foundMeta = foundBands.find(function(r) { return r.role_id === finalSelfId; });
             if (foundMeta) this.roleMetadata = foundMeta;
          }
          this.isPending = false;
        } else {
          this.isPending = true;
        }
      } catch (err) {
        this.isPending = true;
      } finally {
        this.isLoading = false;
      }
    },
    // Formats into exactly €15,800
    formatFullCurr(val) {
      if (!val) return '€0';
      return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val);
    },
    // Formats into €10k, €15k, etc.
    formatK(val) {
        if (!val) return '€0k';
        if (val >= 1000) {
            return '€' + Math.round(val / 1000) + 'k';
        }
        return '€' + val;
    }
  }
};
</script>